import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../components/auth/AuthProvider';
import { createBlogPost, getAdminBlogPost, updateBlogPost } from '../../lib/api';
import { uploadImage } from '../../utils/imageUpload';

const createSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const BlogForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    cover_image: '',
    status: 'draft',
    slug: '',
    excerpt: '',
  });

  const handleTitleChange = (e) => {
    const title = e.target.value;
    const slug = createSlug(title);
    setFormData((prev) => ({ ...prev, title, slug: prev.slug || slug }));
  };

  const fetchBlogPost = useCallback(async () => {
    try {
      const data = await getAdminBlogPost(id);
      if (data) {
        setFormData({
          title: data.title || '',
          content: data.content || '',
          image_url: data.image_url || '',
          cover_image: data.cover_image || '',
          status: data.status || 'draft',
          slug: data.slug || '',
          excerpt: data.excerpt || '',
        });
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      toast.error(error.message || 'Blog yazısı yüklenirken bir hata oluştu');
    }
  }, [id]);

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    if (id) {
      fetchBlogPost();
    }
  }, [id, user, navigate, fetchBlogPost]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const postData = {
        ...formData,
        slug: formData.slug || createSlug(formData.title),
        cover_image: formData.cover_image || formData.image_url,
      };

      if (id) {
        await updateBlogPost(id, postData);
        toast.success('Blog yazısı güncellendi');
      } else {
        await createBlogPost(postData);
        toast.success('Blog yazısı oluşturuldu');
      }

      navigate('/admin/blogs');
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast.error(error.message || 'Blog yazısı kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const imageUrl = await uploadImage(file, 'images', 'blogs');
      setFormData((prev) => ({
        ...prev,
        image_url: imageUrl,
        cover_image: prev.cover_image || imageUrl,
      }));
      toast.success('Görsel yüklendi');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Görsel yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">
        {id ? 'Blog Yazısını Düzenle' : 'Yeni Blog Yazısı'}
      </h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Başlık</label>
          <input
            type="text"
            value={formData.title}
            onChange={handleTitleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#dbc582] focus:border-[#dbc582]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Slug</label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#dbc582] focus:border-[#dbc582]"
            required
          />
          {formData.slug && <p className="mt-1 text-sm text-gray-500">URL: /blogs/{formData.slug}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Özet</label>
          <textarea
            value={formData.excerpt}
            onChange={(e) => setFormData((prev) => ({ ...prev, excerpt: e.target.value }))}
            rows={3}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#dbc582] focus:border-[#dbc582]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">İçerik</label>
          <Editor
            apiKey="qz75q6mextio0tkqwy35aveqrsdxoe1nenicjdwycvb46k2s"
            value={formData.content}
            onEditorChange={(content) => setFormData((prev) => ({ ...prev, content }))}
            init={{
              height: 500,
              menubar: true,
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount',
              ],
              toolbar:
                'undo redo | blocks | bold italic forecolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | removeformat | help',
              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
            }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Kapak Görseli</label>
          <input type="file" accept="image/*" onChange={handleImageUpload} className="mt-1 block w-full" />
          {formData.image_url && (
            <img src={formData.image_url} alt="Kapak görseli" className="mt-2 h-32 object-cover rounded" />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Durum</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#dbc582] focus:border-[#dbc582] sm:text-sm rounded-md"
          >
            <option value="draft">Taslak</option>
            <option value="published">Yayınlandı</option>
          </select>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/blogs')}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#dbc582] hover:bg-[#c5b274] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dbc582]"
          >
            {loading ? 'Kaydediliyor...' : id ? 'Güncelle' : 'Oluştur'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;
